import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Check as ResolveIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../services/api';

const ErrorMonitoring = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalErrors, setTotalErrors] = useState(0);
  const [filters, setFilters] = useState({
    errorType: 'all',
    severity: 'all',
    resolved: 'all'
  });
  const [selectedError, setSelectedError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');

  useEffect(() => {
    loadErrors();
    loadStats();
    // The explicit state dependencies control when the paged query reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters]);

  const loadErrors = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      };

      const response = await api.get('/admin/error-logs', { params });
      setErrors(response.data.errors || []);
      setTotalErrors(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/error-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading error stats:', error);
    }
  };

  const handleViewError = (error) => {
    setSelectedError(error);
    setResolveNotes(error.notes || '');
    setDialogOpen(true);
  };

  const handleResolveError = async () => {
    try {
      await api.put(`/admin/error-logs/${selectedError._id}`, {
        resolved: true,
        notes: resolveNotes
      });
      setDialogOpen(false);
      loadErrors();
      loadStats();
    } catch (error) {
      console.error('Error resolving error log:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'high': return <WarningIcon color="warning" />;
      default: return <InfoIcon color="info" />;
    }
  };

  if (loading && errors.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Error Monitoring
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Errors
              </Typography>
              <Typography variant="h4">
                {stats.totalErrors || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unresolved
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.unresolvedErrors || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Critical
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.errorsBySeverity?.critical || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today
              </Typography>
              <Typography variant="h4">
                {stats.errorsToday || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Critical Errors Alert */}
      {(stats.errorsBySeverity?.critical || 0) > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">
            {stats.errorsBySeverity.critical} Critical Errors Require Immediate Attention
          </Typography>
          <Typography>
            These errors may be affecting user experience and should be investigated immediately.
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Error Type</InputLabel>
              <Select
                value={filters.errorType}
                onChange={(e) => setFilters({ ...filters, errorType: e.target.value })}
                label="Error Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="javascript">JavaScript</MenuItem>
                <MenuItem value="api">API</MenuItem>
                <MenuItem value="database">Database</MenuItem>
                <MenuItem value="authentication">Authentication</MenuItem>
                <MenuItem value="validation">Validation</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                label="Severity"
              >
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.resolved}
                onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="false">Unresolved</MenuItem>
                <MenuItem value="true">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    No error logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              errors.map((error) => (
                <TableRow key={error._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getSeverityIcon(error.severity)}
                      <Chip 
                        label={error.severity} 
                        color={getSeverityColor(error.severity)} 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={error.errorType || 'Unknown'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {error.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {error.user ? `${error.user.firstName} ${error.user.lastName}` : 'System'}
                  </TableCell>
                  <TableCell>
                    {new Date(error.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={error.resolved ? 'Resolved' : 'Open'} 
                      color={error.resolved ? 'success' : 'error'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleViewError(error)}>
                      <ViewIcon />
                    </IconButton>
                    {!error.resolved && (
                      <IconButton onClick={() => handleViewError(error)} color="success">
                        <ResolveIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalErrors}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Error Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent>
          {selectedError && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Error Type</Typography>
                  <Typography>{selectedError.errorType || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Severity</Typography>
                  <Chip 
                    label={selectedError.severity} 
                    color={getSeverityColor(selectedError.severity)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Message</Typography>
                  <Typography>{selectedError.message}</Typography>
                </Grid>
              </Grid>

              {selectedError.stack && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Stack Trace</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {selectedError.stack}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              )}

              {selectedError.requestBody && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Request Data</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(selectedError.requestBody, null, 2)}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              )}

              <TextField
                fullWidth
                label="Resolution Notes"
                multiline
                rows={4}
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Describe how this error was resolved..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {selectedError && !selectedError.resolved && (
            <Button onClick={handleResolveError} variant="contained" color="success">
              Mark as Resolved
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorMonitoring;
